/**
 * Formats a price value with currency symbol and proper decimal places
 *
 * @param {number} price - The price value (e.g., 160.00 = $160.00)
 * @param {string} currency - The currency code (e.g., 'USD', 'EUR')
 * @returns {string} Formatted price string (e.g., '$19.99')
 */
export function formatPrice(price, currency) {
  if (typeof price !== 'number' || !currency || typeof currency !== 'string') {
    return '';
  }

  // Validate currency code format (should be 3 uppercase letters)
  const currencyCode = currency.trim().toUpperCase();
  if (currencyCode.length !== 3 || !/^[A-Z]{3}$/.test(currencyCode)) {
    return '';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  } catch (error) {
    // If currency code is invalid, fall back to showing price without formatting
    console.warn(`Invalid currency code: ${currency}`, error);
    return '';
  }
}

/**
 * Checks if a product has valid price information to display
 *
 * @param {object} product - The product object from Firestore
 * @returns {boolean} True if the product has valid price info
 */
export function hasValidPrice(product) {
  return (
    product?.priceInfo?.price != null &&
    typeof product.priceInfo.price === 'number' &&
    product.priceInfo.price > 0 &&
    product.priceInfo.currency
  );
}

/**
 * Gets the formatted price for a product if available
 *
 * @param {object} product - The product object from Firestore
 * @returns {string|null} Formatted price string or null if no valid price
 */
export function getFormattedProductPrice(product) {
  if (!hasValidPrice(product)) {
    return null;
  }

  return formatPrice(product.priceInfo.price, product.priceInfo.currency);
}
